/**
 * Outbound link decoration. Adds the current UTM params to anchor hrefs
 * pointing at `targetHost`. A MutationObserver re-decorates when new
 * anchors are added, debounced to 100ms.
 */

import { resolveUtms } from './attribution';

const DEBOUNCE_MS = 100;

export function startLinkDecoration(targetHost: string): MutationObserver | undefined {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return undefined;
  if (!targetHost) return undefined;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const run = (): void => {
    timer = null;
    decorateAll(targetHost);
  };
  const schedule = (): void => {
    if (timer !== null) return;
    timer = setTimeout(run, DEBOUNCE_MS);
  };
  decorateAll(targetHost);
  const observer = new MutationObserver(schedule);
  try {
    observer.observe(document.documentElement, { childList: true, subtree: true });
  } catch {
    /* observe failed */
  }
  return observer;
}

function decorateAll(targetHost: string): void {
  try {
    const utm = resolveUtms();
    if (Object.keys(utm).length === 0) return;
    const anchors = document.querySelectorAll<HTMLAnchorElement>('a[href]');
    anchors.forEach((a) => decorateAnchor(a, targetHost, utm));
  } catch {
    /* best-effort */
  }
}

function decorateAnchor(
  a: HTMLAnchorElement,
  targetHost: string,
  utm: Record<string, string | undefined>
): void {
  try {
    const href = a.getAttribute('href');
    if (!href) return;
    let url: URL;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }
    // Exact host or true subdomain only. A naive substring match would also
    // accept attacker-controlled hosts like `get.ignitionbenefits.com.evil.com`,
    // sending first-touch UTMs to a third party.
    const host = url.hostname.toLowerCase();
    const target = targetHost.toLowerCase();
    if (host !== target && !host.endsWith('.' + target)) return;
    let changed = false;
    for (const [k, v] of Object.entries(utm)) {
      if (!v) continue;
      if (url.searchParams.has(k)) continue;
      url.searchParams.set(k, v);
      changed = true;
    }
    if (changed) a.setAttribute('href', url.toString());
  } catch {
    /* skip this anchor */
  }
}
